import React, { useCallback, useContext, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
// Services

import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import Button from "../ui-components/Button";
import Input from "../ui-components/Input";
import Modal from "../ui-components/Modal";
import { NotificationsContext } from "../ui-components/Notifications";
import Select from "../ui-components/Select";
import Toggle from "../ui-components/Toggle";
import ArrayOfStringInput from "./ArrayOfStringInput";
import {ExclamationCircleIcon} from "@heroicons/react/24/outline";

function DbModelCreateRow({
  open,
  toggleOpen,
  data,
  setData,
  apiKey,
  columns,
  publicTable,
  selectedParent,
  setSelectedParent,
  parentField,
  parentDescriptionField,
  fieldsLockedByParent,
}) {
  const intl = useIntl();
  const [newRow, setNewRow] = useState({
    ...(columns.filter((c) => c.type === "string_array").length
      ? columns
          .filter((c) => c.type === "string_array")
          .reduce((acc, c) => ({ ...acc, [c.field]: [] }), {})
      : {}),
    ...(columns.filter((c) => c.type === "boolean").length
      ? columns
          .filter((c) => c.type === "boolean")
          .reduce((acc, c) => ({ ...acc, [c.field]: false }), {})
      : {}),
    ...(columns.filter((c) => c.type === "select").length
      ? columns
          .filter((c) => c.type === "select")
          .reduce((acc, c) => ({ ...acc, [c.field]: Object.keys(c.lookup)[0] }), {})
      : {}),
    ...(
      selectedParent && fieldsLockedByParent
        ? columns
            .filter((c) => c.type === "string" && fieldsLockedByParent.includes(c.field))
            .reduce((acc, c) => ({...acc, [c.field]: selectedParent[c.field]}), {})
        : {}
    )
  });
  const [loading, setLoading] = useState(false);
  const { push } = useContext(NotificationsContext);
  const apiRequest = useCountryAxiosInstance();

  const renderOptions = (name, lookup) => [{name: "Seleziona tipologia", value: '', disabled: true}]
    .concat(Object.entries(lookup).reduce((prev, [value, name]) => [...prev, {value, name}], []))
    //.map((opt) => ({...opt, name: intl.formatMessage({id: opt.name})}))

  const onSubmit = useCallback(
    async (e) => {
      try {
        e.preventDefault();

        setLoading(true);
        // let payload = apiKey === 'services' ? {...newRow, type: 'configurator'} : newRow
        let payload = Object.entries(newRow)
          .reduce(
            (prev, [field, value]) => ({
              ...prev,
              [field]: !(columns.find((c) => c.field === field)?.auto ?? false) ? value : null
            }),
            {}
          );
        payload =
          apiKey === "services"
            ? {
                associated_services: {},
                active_version: 1,
                metadata_: {},
                ...payload,
              }
            : payload;
        payload = apiKey === 'locale' ? {...payload, service: 'global'} : payload
        payload = {
          ...payload,
          r_perm: [['services'].includes(apiKey) ? 'public' : 'admin'],  // services must be public in read
          w_perm: ['admin']
        }
        if (selectedParent)
          payload[parentField] = selectedParent['id'];

        const newData = await apiRequest.post(`/${apiKey}`, payload);
        setData([...data, newData]);

        if (!selectedParent)
          toggleOpen();
        else
          setSelectedParent(null);

        push({
          title: "Created",
          type: "success",
        });
      } catch (error) {
        push({
          title: intl.formatMessage({ id: "already_used" }),
          type: "error",
        });
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [setData, toggleOpen, data, push, intl, apiRequest, apiKey, newRow, parentField, selectedParent]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (!open) {
    return null;
  }

  return (
    <div>
      <Modal onExit={toggleOpen}>
        <div className="m-8">
          <h3 className="text-center text-2xl font-extrabold text-black-500">
            <FormattedMessage id="add" />
          </h3>
        </div>

        <form id="add-form" onSubmit={onSubmit}>
          {columns
            .filter((c) => !c.auto)
            .map(({ type, field, title, required, lookup }) => {
              switch (type) {
                case "string":
                case "number":
                  return (
                    <Input
                      key={field}
                      value={newRow[field]}
                      onChange={(e) => {
                        setNewRow({ ...newRow, [field]: e.target.value });
                      }}
                      label={title}
                      type={type === "number" ? "number" : "text"}
                      errorMessage={`Wrong ${title}`}
                      required={required}
                      disabled={!!selectedParent && fieldsLockedByParent?.includes(field)}
                    />
                  );
                case "select":
                  return (
                    <Select
                      key={field}
                      label={title}
                      options={renderOptions(field, lookup)}
                      value={newRow[field]}
                      onChange={(e) => {
                        setNewRow({ ...newRow, [field]: e.target.value });
                      }}
                    />
                  );
                case "boolean":
                  return (
                    <Toggle
                      key={field}
                      label={title}
                      checked={newRow[field]}
                      onChange={() =>
                        setNewRow({ ...newRow, [field]: !newRow[field] })
                      }
                    />
                  );
                case "string_array": {
                  const hidden =
                    (field === "r_perm" || field === "w_perm") && publicTable
                      ? "hidden"
                      : "";
                  return (
                    <div className={`${hidden} w-full`}>
                      <ArrayOfStringInput
                        key={field}
                        setValue={(value) =>
                          setNewRow({ ...newRow, [field]: value })
                        }
                        value={newRow[field]}
                        label={title}
                      />
                    </div>
                  );
                }

                default:
                  return null;
              }
            })}

          {selectedParent && (
            <p className="flex gap-1 items-center my-3">
              <ExclamationCircleIcon className="w-5 mr-1" />
              <span>
                <FormattedMessage id="create_as_child_element_of" />:
              </span>
              <span className="font-bold">
                {selectedParent?.[parentDescriptionField] ?? "Unknown"}
              </span>
            </p>
          )}

          <div className="mt-4 flex items-end justify-end">
            <Button submitting={loading} type="submit">
              <FormattedMessage id="add" />
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default DbModelCreateRow;
